import T from 'i18n-react';
import { toUtcLongDate } from '../utils/date-helpers';
import featureFlags, { FEATURE_FLAGS } from './featureFlags';
import { getStatusFromServerStatus } from "./transition-helpers";
import { ISSUE_ATTRIBUTE_NAMES } from '../constants/issue-attribute-names';

// All the functions in markupMap return marked-up STRINGS -- not jsx:
//     ...eg: use "class" -- not "className"
// The keys in the map correspond to a property name in `changeset.changes`
// The 2nd argument, isNewValue, signals whether it is the 1st or 2nd value
//     from the associated `changeset.changes[propertyName]` Array.
// The results from the function calls are injected into i18next template strings.
const markupMap = {
  status(status, _isNewValue) {
    const normalizeStatus = getStatusFromServerStatus(status);
    return `<span class="ChangesetDetails__status status-${normalizeStatus}">${T.translate(`status.${normalizeStatus}`)}</span>`;
  },
  assignedTo(assignee, _isNewValue) {
    // Note: Fix assignee incorrect "null" value got from backend (should be null).
    assignee = assignee === 'null' ? null : assignee;
    return `<span class="ChangesetDetails__assignee-name">${assignee}</span>`;
  },
  dueDate(dueDate, _isNewValue) {
    return `<span class="ChangesetDetails__due-date">${toUtcLongDate(dueDate)}</span>`;
  },
  coReviewers(coReviewer, _isNewValue) {
    return `<span class="ChangesetDetails__assignee-name">${coReviewer}</span>`;
  },
  distributionList(distributionList, _isNewValue) {
    return `<span class="ChangesetDetails__assignee-name">${distributionList}</span>`;
  },
};

const emptyFunc = (a) => a;

const buildI18nParams = (changes) => {
  const i18nParams = {};
  const changesetAttributes = [
    'status', 'answer', 'dueDate', 'assignedTo', 'assignedToType', 'workflowStateId', 'coReviewers',
    'distributionList', 'pushpinAttributes', 'title', 'description', 'suggestedAnswer', 'locationDescription',
    'customIdentifier', 'discipline', 'priority', 'costImpact', 'scheduleImpact', 'category', 'reference', 'linkedDocument', 'link', 'officialResponse',
  ];
  changesetAttributes.forEach((attribute) => {
    const valuePair = changes[attribute];
    if (valuePair) {
      let [oldValue, newValue] = valuePair;
      if (attribute === 'coReviewers' || attribute === 'distributionList' || attribute === 'discipline' || attribute === 'category') {
        // We expect newValue and oldValue to be an array for these attributes. The BE used to allow
        // null values (and having trouble with fixing the changesets for those) so we have to fix that in the client
        if (!oldValue) {
          oldValue = [];
        }
        if (!newValue) {
          newValue = [];
        }
        const wrapperFunction = markupMap[attribute] || emptyFunc;
        const added = newValue.filter(e => !oldValue.includes(e)).map(a => wrapperFunction(a));
        const removed = oldValue.filter(e => !newValue.includes(e)).map(a => wrapperFunction(a));
        i18nParams[`${attribute}Added`] = added.join(', ');
        i18nParams[`${attribute}Removed`] = removed.join(', ');
      } else {
        const wrapperFunction = markupMap[attribute];
        i18nParams[`${attribute}Old`] = oldValue && wrapperFunction ? wrapperFunction(oldValue, false) : oldValue;
        i18nParams[`${attribute}New`] = newValue && wrapperFunction ? wrapperFunction(newValue, true) : newValue;
      }
    }
  });
  return i18nParams;
};

// eslint-disable-next-line consistent-return
const getDescriptionI18nKey = (event, i18nParams) => {
  let key = '';
  const { statusNew, answerNew, dueDateOld, dueDateNew, assignedToNew, workflowStateIdNew,
    coReviewersAdded, coReviewersRemoved, distributionListAdded, distributionListRemoved,
    pushpinAttributesNew, pushpinAttributesOld, titleNew, descriptionNew, descriptionOld,
    suggestedAnswerNew, suggestedAnswerOld, locationDescriptionNew, locationDescriptionOld,
    customIdentifierNew, categoryAdded, categoryRemoved, disciplineAdded, disciplineRemoved,
    costImpactNew, costImpactOld, scheduleImpactNew, scheduleImpactOld, priorityNew, priorityOld,
    referenceNew, referenceOld, linkedDocumentNew, linkedDocumentOld, linkOld, linkNew } = i18nParams;
  // eslint-disable-next-line default-case
  switch (event) {
    case 'create':
      if (statusNew) {
        // Creation w/ Assignee: 'assignedTo'
        // Creation w/o Assignee: 'status
        key = `${assignedToNew ? 'assignedTo' : 'status'}`;
      }
      break;
    case 'update':
      if (statusNew) {
        // Status Change (w/o official response): 'status'
        // Status Change w/ Official Response (current): 'statusWithAnswer'
        key = `status${answerNew ? 'WithAnswer' : ''}`;
      } else if (answerNew) {
        // Only admin can do this?
        key = 'answer';
      } else if (assignedToNew) {
        // Assignment Change (& Assignment Change to no one)
        key = 'assignedTo';
      } else if (dueDateOld && !dueDateNew) {
        // Due Date clear
        key = 'clearDueDate';
      } else if (dueDateNew) {
        // Due Date Change
        key = 'dueDate';
      } else if (workflowStateIdNew) {
        key = 'workflowStateId';
      } else if (coReviewersAdded || coReviewersRemoved) {
        if (coReviewersAdded && coReviewersRemoved) {
          key = 'coReviewers.both';
        } else if (coReviewersAdded) {
          key = 'coReviewers.added';
        } else {
          key = 'coReviewers.removed';
        }
      } else if (distributionListAdded || distributionListRemoved) {
        if (distributionListAdded && distributionListRemoved) {
          key = 'distributionList.both';
        } else if (distributionListAdded) {
          key = 'distributionList.added';
        } else {
          key = 'distributionList.removed';
        }
      } else if (categoryAdded || categoryRemoved) {
        if (categoryAdded && categoryRemoved) {
          key = 'category.both';
        } else if (categoryAdded) {
          key = 'category.added';
        } else {
          key = 'category.removed';
        }
      } else if (disciplineAdded || disciplineRemoved) {
        if (disciplineAdded && disciplineRemoved) {
          key = 'discipline.both';
        } else if (disciplineAdded) {
          key = 'discipline.added';
        } else {
          key = 'discipline.removed';
        }
      } else if (linkedDocumentNew) {
        key = 'pushpin.place';
      } else if (linkedDocumentOld) {
        key = 'pushpin.delete';
      } else if (pushpinAttributesNew || pushpinAttributesOld) {
        const hiddenDocVersionAttrNAme = ISSUE_ATTRIBUTE_NAMES.HIDDEN_DOC_VERSION;

        if (pushpinAttributesNew && pushpinAttributesNew[hiddenDocVersionAttrNAme]) {
          key = 'hide.hidden';
        } else if (pushpinAttributesOld && pushpinAttributesOld[hiddenDocVersionAttrNAme]) {
          key = 'hide.visible';
        }
      } else if (titleNew) {
        key = 'title';
      } else if (suggestedAnswerOld && !suggestedAnswerNew) {
        key = 'clearSuggestedAnswer';
      } else if (suggestedAnswerNew) {
        key = 'suggestedAnswer';
      } else if (locationDescriptionOld && !locationDescriptionNew) {
        key = 'clearLocationDescription';
      } else if (locationDescriptionNew) {
        key = 'locationDescription';
      } else if (descriptionOld && !descriptionNew) {
        key = 'clearDescription';
      } else if (descriptionNew) {
        key = 'description';
      } else if (customIdentifierNew) {
        key = 'customIdentifier';
      } else if (costImpactOld && !costImpactNew) {
        key = 'clearCostImpact';
      } else if (costImpactNew) {
        key = 'costImpact';
      } else if (scheduleImpactOld && !scheduleImpactNew) {
        key = 'clearScheduleImpact';
      } else if (scheduleImpactNew) {
        key = 'scheduleImpact';
      } else if (priorityOld && !priorityNew) {
        key = 'clearPriority';
      } else if (priorityNew) {
        key = 'priority';
      } else if (referenceOld && !referenceNew) {
        key = 'clearReference';
      } else if (referenceNew) {
        key = 'reference';
      } else if (linkOld && !linkNew) {
        key = 'unlink';
      } else if (linkNew) {
        key = 'link';
      }
  }
  if (key) {
    return `changeset.${event}.${key}`;
  }
};

export const localizeChangesetDisplayNames = function(changes) {
  const { assignedTo: assignedToOriginal } = changes;
  const assignedTo = assignedToOriginal ? [
    assignedToOriginal[0] || T.translate('user.unassigned'),
    assignedToOriginal[1] || T.translate('user.unassigned'),
  ] : null;
  return { ...changes, ...{ assignedTo }};
};

export const getChangesetClientAttributes = (changeset) => {
  const { event, changesWithOxygen, isLastOfficialResponse } = changeset;
  const changes = localizeChangesetDisplayNames({ ...changesWithOxygen, isLastOfficialResponse });
  const i18nParams = buildI18nParams(changes);
  const i18nKey = getDescriptionI18nKey(event, i18nParams);
  const isPushpinChange = event === 'update' && (i18nParams.pushpinAttributesNew || i18nParams.pushpinAttributesOld);
  const showChangeset = isPushpinChange || !!i18nKey;
  const attributes = {};
  if (showChangeset) {
    return {
      i18nKey,
      i18nParams,
      isPushpinChange,
    };
  }
  return attributes;
};
